import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Server, Box, Check, Cpu, Terminal, Rocket } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface Container {
    id: string;
    label: string;
    color: string;
    status: 'pending' | 'deployed';
}

export const DockerContainers = () => {
    const { completeModule } = useAcademyStore();
    const [rack, setRack] = useState<Container[]>([]);
    const [completed, setCompleted] = useState(false);

    const availableContainers: Omit<Container, 'status'>[] = [
        { id: 'web', label: 'Web Server (React)', color: 'bg-blue-500' },
        { id: 'db', label: 'Database (Postgres)', color: 'bg-green-500' },
        { id: 'cache', label: 'Cache (Redis)', color: 'bg-red-500' },
        { id: 'api', label: 'API Backend', color: 'bg-purple-500' },
    ];

    const addContainer = (c: Omit<Container, 'status'>) => {
        if (rack.length >= 4) return;
        setRack([...rack, { ...c, status: 'pending' }]);

        // Simulate "Spinning Up"
        setTimeout(() => {
            setRack(prev => prev.map(item => item.id === c.id ? { ...item, status: 'deployed' } : item));

            // Check if we have deployed at least 3 containers to complete
            if (rack.length + 1 >= 3 && !completed) {
                setCompleted(true);
                completeModule('docker-containers', 100);
            }
        }, 1200);
    };

    const removeContainer = (idx: number) => {
        setRack(rack.filter((_, i) => i !== idx));
    };

    return (
        <div className="min-h-screen bg-[#f0f9ff] flex flex-col p-8 font-sans pt-24 pb-20">

            <div className="text-center mb-8">
                <h1 className="text-4xl font-black font-comic text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Server className="text-cyan-600" size={40} />
                    Docker Containers 📦
                </h1>
                <div className="bg-cyan-100 text-cyan-800 px-4 py-1 rounded-full text-sm font-bold border border-cyan-200 inline-block mb-4">
                    Module: The Server Rack
                </div>
                <p className="text-slate-600 max-w-2xl mx-auto mb-6">
                    Servers used to be messy. Now we use <strong>Containers</strong>!
                    <br />It's like shipping boxes: Everything the app needs is packed inside.
                </p>

                {/* Mission Control Panel */}
                <div className="max-w-2xl mx-auto bg-white p-6 rounded-2xl shadow-lg border-l-4 border-cyan-500 text-left animate-fade-in-up">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2">
                        <Terminal size={18} className="text-cyan-600" /> Mission Brief
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                        Your server rack is empty! We need to deploy a full stack application.
                    </p>
                    <div className="flex gap-4 items-center bg-cyan-50 p-3 rounded-lg border border-cyan-100">
                        <Rocket className="text-cyan-500 animate-pulse" />
                        <div className="text-sm font-bold text-cyan-800">
                            Objective: Click the buttons below to run `docker run` and fill at least 3 slots!
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mt-8">

                {/* LEFT: Dockyard (Selection) */}
                <div className="bg-white p-8 rounded-3xl shadow-xl border-4 border-slate-100 relative">
                    <div className="absolute top-0 right-0 opacity-10 pointer-events-none p-4">
                        <img src="/assets/gorillas/sys_admin.png" className="w-32" onError={(e) => e.currentTarget.style.display = 'none'} />
                    </div>

                    <h3 className="font-bold text-slate-700 mb-6 flex items-center gap-2">
                        <Box /> Available Images
                    </h3>

                    <div className="grid grid-cols-1 gap-4">
                        {availableContainers.map(c => (
                            <button
                                key={c.id}
                                onClick={() => addContainer(c)}
                                disabled={rack.length >= 4 || rack.some(r => r.id === c.id)}
                                className={`p-4 rounded-xl border-2 flex items-center justify-between transition-all group ${rack.some(r => r.id === c.id)
                                    ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-100'
                                    : 'bg-white border-slate-200 hover:border-cyan-400 hover:shadow-lg hover:-translate-y-1'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full ${c.color} flex items-center justify-center text-white shadow-sm`}>
                                        <Box size={16} />
                                    </div>
                                    <div className="text-left">
                                        <div className="font-bold text-slate-700">{c.label}</div>
                                        <div className="text-[10px] text-slate-400">image: {c.id}:latest</div>
                                    </div>
                                </div>
                                <div className="bg-slate-100 px-3 py-1.5 rounded text-xs font-mono font-bold text-slate-500 group-hover:bg-cyan-100 group-hover:text-cyan-700 transition-colors">
                                    docker run {c.id}
                                </div>
                            </button>
                        ))}
                    </div>

                    {rack.length === 4 && (
                        <div className="mt-4 text-center text-red-500 font-bold text-sm bg-red-50 p-2 rounded-lg border border-red-100 animate-pulse">
                            🚨 Rack is full! (Free Tier Limit 😅)
                        </div>
                    )}
                </div>

                {/* RIGHT: The Server Rack */}
                <div className="relative flex justify-center">
                    <div className="w-80 bg-slate-800 rounded-lg p-6 pb-2 border-y-8 border-x-4 border-slate-700 shadow-2xl relative">
                        {/* Rack Rails */}
                        <div className="absolute top-0 bottom-0 left-2 w-2 bg-black/30 border-r border-slate-600"></div>
                        <div className="absolute top-0 bottom-0 right-2 w-2 bg-black/30 border-l border-slate-600"></div>

                        {/* Header */}
                        <div className="flex justify-between items-center mb-6 text-cyan-400 font-mono text-xs border-b border-slate-700 pb-2">
                            <span>SERVER-01</span>
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                ONLINE
                            </div>
                        </div>

                        {/* Slots */}
                        <div className="space-y-4 min-h-[300px]">
                            <AnimatePresence>
                                {rack.map((container, idx) => (
                                    <motion.div
                                        key={container.id}
                                        initial={{ x: -200, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        layout
                                        className="relative h-16 bg-slate-700 rounded border border-slate-600 flex items-center px-4 justify-between group cursor-pointer hover:bg-slate-600 hover:border-red-400 transition-colors shadow-lg"
                                        onClick={() => removeContainer(idx)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {container.status === 'pending' ? (
                                                <div className="w-3 h-3 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin"></div>
                                            ) : (
                                                <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]"></div>
                                            )}
                                            <span className="font-mono text-white text-sm font-bold truncate w-28">{container.label}</span>
                                        </div>

                                        <div className={`text-[10px] font-bold px-2 py-0.5 rounded ${container.color} text-white/90`}>
                                            :8080
                                        </div>

                                        <div className="absolute inset-0 flex items-center justify-center bg-red-500/90 text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm rounded">
                                            STOP & REMOVE
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {/* Empty Slots Filler */}
                            {[...Array(4 - rack.length)].map((_, i) => (
                                <motion.div
                                    layout
                                    key={`empty-${i}`}
                                    className="h-16 border-2 border-dashed border-slate-600/50 rounded flex items-center justify-center text-slate-600 font-mono text-xs"
                                >
                                    [EMPTY SLOT U-{4 - rack.length - i}]
                                </motion.div>
                            ))}
                        </div>

                        {/* Footer Lights */}
                        <div className="mt-8 flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="w-1 h-4 bg-blue-500/30 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.1}s` }}></div>
                            ))}
                        </div>
                    </div>
                </div>

            </div>

            {/* Educational Content Section */}
            <div className="max-w-6xl mx-auto w-full mt-12 space-y-8">

                {/* Theory Section */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border-2 border-cyan-100">
                    <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                        📚 What Are Containers?
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg text-slate-700 mb-3">The Problem Containers Solve</h3>
                            <p className="text-slate-600 mb-4">
                                "It works on my machine!"<br />
                                This phrase haunted developers for decades. Your app worked locally but broke in production
                                because of different OS versions, library conflicts, or missing dependencies.
                            </p>
                            <p className="text-slate-600 mb-4">
                                <strong>Containers solve this</strong> by packaging your code <em>with everything it needs to run</em>
                                into a single, portable unit. It's like shipping your entire kitchen instead of just the recipe!
                            </p>

                            <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-200 mt-4">
                                <h4 className="font-bold text-cyan-800 mb-2">📦 A Container Includes:</h4>
                                <ul className="text-cyan-700 text-sm space-y-1">
                                    <li>• Your application code</li>
                                    <li>• Runtime (Node.js, Python, etc.)</li>
                                    <li>• System libraries & dependencies</li>
                                    <li>• Environment variables & config</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                            <h3 className="font-bold text-lg text-slate-700 mb-3">⚔️ Containers vs Virtual Machines</h3>

                            <div className="space-y-4">
                                <div className="bg-white p-4 rounded-lg border border-slate-300">
                                    <div className="font-bold text-slate-700 mb-2">🖥️ Virtual Machine (VM)</div>
                                    <ul className="text-slate-600 text-sm space-y-1">
                                        <li>• Full copy of an entire OS</li>
                                        <li>• Takes GB of disk space</li>
                                        <li>• Minutes to start</li>
                                        <li>• Complete isolation</li>
                                    </ul>
                                </div>

                                <div className="bg-white p-4 rounded-lg border-2 border-cyan-400">
                                    <div className="font-bold text-cyan-700 mb-2">📦 Container (Docker)</div>
                                    <ul className="text-slate-600 text-sm space-y-1">
                                        <li>• Shares the host OS kernel</li>
                                        <li>• Takes MB of disk space</li>
                                        <li>• Starts in seconds</li>
                                        <li>• Process-level isolation</li>
                                    </ul>
                                </div>
                            </div>

                            <p className="text-slate-600 text-sm mt-4">
                                <strong>Rule of thumb:</strong> Use VMs when you need complete isolation (different OS).
                                Use containers when you want lightweight, fast deployments!
                            </p>
                        </div>
                    </div>
                </div>

                {/* Docker Commands */}
                <div className="bg-slate-900 p-8 rounded-3xl text-white font-mono">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3 font-sans">
                        💻 Essential Docker Commands
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-black/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-cyan-400 text-sm mb-2"># Pull an image from Docker Hub</div>
                            <div className="text-white">docker pull <span className="text-green-400">postgres:latest</span></div>
                        </div>
                        <div className="bg-black/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-cyan-400 text-sm mb-2"># Run a container</div>
                            <div className="text-white">docker run <span className="text-yellow-400">-d -p 8080:80</span> <span className="text-green-400">nginx</span></div>
                        </div>
                        <div className="bg-black/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-cyan-400 text-sm mb-2"># List running containers</div>
                            <div className="text-white">docker ps</div>
                        </div>
                        <div className="bg-black/50 p-4 rounded-xl border border-slate-700">
                            <div className="text-cyan-400 text-sm mb-2"># Stop a container</div>
                            <div className="text-white">docker stop <span className="text-orange-400">container_id</span></div>
                        </div>
                        <div className="bg-black/50 p-4 rounded-xl border border-slate-700 md:col-span-2">
                            <div className="text-cyan-400 text-sm mb-2"># Build from Dockerfile</div>
                            <div className="text-white">docker build <span className="text-yellow-400">-t myapp:v1</span> .</div>
                        </div>
                    </div>
                </div>

                {/* Dockerfile Example */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border-2 border-cyan-100">
                    <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                        📄 Anatomy of a Dockerfile
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-slate-900 p-6 rounded-xl font-mono text-sm overflow-x-auto">
                            <div className="text-slate-400"># Example: Node.js App</div>
                            <div className="mt-2">
                                <span className="text-purple-400">FROM</span> <span className="text-green-400">node:18-alpine</span>
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">WORKDIR</span> <span className="text-yellow-400">/app</span>
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">COPY</span> package*.json ./
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">RUN</span> npm install
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">COPY</span> . .
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">EXPOSE</span> <span className="text-cyan-400">3000</span>
                            </div>
                            <div className="mt-2">
                                <span className="text-purple-400">CMD</span> [<span className="text-green-400">"npm"</span>, <span className="text-green-400">"start"</span>]
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                <strong className="text-purple-700">FROM</strong>
                                <p className="text-slate-600 text-sm">Base image - what OS/runtime to start from</p>
                            </div>
                            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                <strong className="text-yellow-700">WORKDIR</strong>
                                <p className="text-slate-600 text-sm">Sets the working directory inside the container</p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <strong className="text-blue-700">COPY / RUN</strong>
                                <p className="text-slate-600 text-sm">Copy files in, run commands to install dependencies</p>
                            </div>
                            <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
                                <strong className="text-cyan-700">EXPOSE</strong>
                                <p className="text-slate-600 text-sm">Documents which port the container listens on</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                <strong className="text-green-700">CMD</strong>
                                <p className="text-slate-600 text-sm">The default command when the container starts</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Docker Compose */}
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-8 rounded-3xl border-2 border-cyan-100">
                    <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                        🎼 Docker Compose: Orchestrating Multiple Containers
                    </h2>

                    <p className="text-slate-600 mb-6">
                        Real apps need multiple services: a web server, database, cache, etc.
                        <strong> Docker Compose</strong> lets you define and run multi-container applications with a single YAML file.
                    </p>

                    <div className="bg-slate-900 p-6 rounded-xl font-mono text-sm">
                        <div className="text-slate-400"># docker-compose.yml</div>
                        <div className="text-purple-400 mt-2">version: <span className="text-green-400">'3.8'</span></div>
                        <div className="text-purple-400 mt-2">services:</div>
                        <div className="ml-4 text-yellow-400">web:</div>
                        <div className="ml-8 text-white">build: .</div>
                        <div className="ml-8 text-white">ports: [<span className="text-green-400">"3000:3000"</span>]</div>
                        <div className="ml-4 text-yellow-400 mt-2">db:</div>
                        <div className="ml-8 text-white">image: <span className="text-green-400">postgres:15</span></div>
                        <div className="ml-8 text-white">environment:</div>
                        <div className="ml-12 text-cyan-400">POSTGRES_PASSWORD: secret</div>
                        <div className="ml-4 text-yellow-400 mt-2">redis:</div>
                        <div className="ml-8 text-white">image: <span className="text-green-400">redis:alpine</span></div>
                    </div>

                    <div className="mt-6 bg-white p-4 rounded-xl border border-cyan-200">
                        <p className="text-slate-600 text-sm">
                            <strong>One command to rule them all:</strong> <code className="bg-slate-100 px-2 py-1 rounded">docker-compose up</code>
                            starts your entire stack! Add <code className="bg-slate-100 px-2 py-1 rounded">-d</code> to run in background.
                        </p>
                    </div>
                </div>

                {/* Pro Tips */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-3xl border-2 border-green-200">
                    <h2 className="text-2xl font-black text-green-800 mb-6 flex items-center gap-3">
                        💡 Docker Pro Tips
                    </h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-green-200">
                            <h3 className="font-bold text-slate-700 mb-2">🏔️ Use Alpine Images</h3>
                            <p className="text-slate-600 text-sm">
                                <code className="bg-slate-100 px-1 rounded">node:18-alpine</code> is ~50MB vs <code className="bg-slate-100 px-1 rounded">node:18</code> at ~900MB.
                                Smaller images = faster deployments!
                            </p>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-green-200">
                            <h3 className="font-bold text-slate-700 mb-2">📦 Multi-Stage Builds</h3>
                            <p className="text-slate-600 text-sm">
                                Build in one stage (with dev dependencies), then copy only production files to a slim final image.
                            </p>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-green-200">
                            <h3 className="font-bold text-slate-700 mb-2">🔐 Never Put Secrets in Images</h3>
                            <p className="text-slate-600 text-sm">
                                Use environment variables or Docker secrets. Anyone can inspect image layers!
                            </p>
                        </div>
                        <div className="bg-white p-5 rounded-xl shadow-sm border border-green-200">
                            <h3 className="font-bold text-slate-700 mb-2">💾 Use Volumes for Data</h3>
                            <p className="text-slate-600 text-sm">
                                Containers are ephemeral! Use <code className="bg-slate-100 px-1 rounded">-v</code> volumes to persist database data.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {completed && (
                <ModuleSuccess
                    title="Container Captain"
                    nextModulePath="/"
                    onRetry={() => {
                        setCompleted(false);
                        setRack([]);
                    }}
                >
                    <div className="bg-cyan-50 p-4 rounded-xl text-left border border-cyan-200">
                        <h4 className="font-bold text-cyan-800 mb-2">📦 Stack Deployed!</h4>
                        <p className="text-cyan-700 text-sm">
                            Your containers are running isolated and efficient. You just saved hours of "it works on my machine" debugging!
                        </p>
                    </div>
                </ModuleSuccess>
            )}

        </div>
    );
};
